import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Divider,
    styled,
} from '@mui/material'
import {Colors, Texts} from 'appearance'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useI18n, useVerifyOrgCodeMutation} from 'store/Store'
import {settings} from 'constants/settings'
import Button from 'components/Control/Button'
import EditIcon from '@mui/icons-material/Edit'
import EditableInput from 'components/Control/EditableInput'
import {imageToBase64} from 'services/utils/misc'
import {useForm} from 'react-hook-form'
import {FormErrorText} from 'components/Control/Form'
import analyticsService from 'services/analytic'

const FormSettingsItem = ({
    itemName,
    item,
    allowEdit,
    useSaveItemMutation,
    photoFieldName,
    readOnlyInputs,
    isMobile,
}) => {
    const imageInputRef = useRef(null)
    const {i18n} = useI18n()
    const [editMode, setEditMode] = useState(false)
    const [image, setImage] = useState(null)
    const [error, setError] = useState()
    const titles = settings[itemName]
    const [doSave, {error: saveError, data: saveResponse}] =
        useSaveItemMutation()

    const [doVerifyOrgCode] = useVerifyOrgCodeMutation()

    const hasOrgCode = useMemo(() => !!item.Insurance_org_Code, [item])

    const {
        control: formControl,
        formState: {errors: formErrors},
        setValue,
        handleSubmit,
        reset,
    } = useForm({defaultValues: {...item}})

    const onSubmit = async (values) => {
        const {id, phone, ...body} = values

        let res = {success: true}
        if (!hasOrgCode && body.Insurance_org_Code) {
            const response = await doVerifyOrgCode({
                orgCode: body.Insurance_org_Code,
            })

            res = response?.data || response?.error
        }
        if (res?.success) {
            doSave(body)
            toggleEdit()
            return
        }
        // alert(res?.msg || res?.data || 'שגיאה בקוד ארגון')
    }

    const toggleEdit = () => {
        setEditMode(!editMode)
        console.log('analytics toggle edit')
        if (!editMode) {
            analyticsService.profileSettingsPressed('edit')
        }
    }

    const onCancel = () => {
        toggleEdit()
        reset()
        setImage(null)
        analyticsService.profileSettingsPressed('cancel')
    }

    const onUpload = async (event) => {
        setImage(URL.createObjectURL(event.target.files[0]))
        try {
            const base64Image = await imageToBase64(event.target.files[0])
            setValue(photoFieldName, base64Image)
        } catch (error) {
            console.log('error converting image', {error})
            setError(i18n.t('settings.image_upload_error'))
        }
    }

    useEffect(() => {
        if (saveError) {
            setError(saveError?.data?.message)
        }
    }, [saveError])

    useEffect(() => {
        if (saveResponse) {
            reset({...saveResponse})
        }
    }, [saveResponse, reset])

    return (
        <Container>
            <ItemHeader expandIcon={<ExpandMoreIcon />} id={itemName}>
                <Title>
                    <Avatar
                        src={item[photoFieldName]}
                        sx={{bgcolor: 'secondary.main'}}
                    />
                    <TitleText>{i18n.t(`settings.${itemName}`)}</TitleText>
                </Title>
            </ItemHeader>
            <Content isMobile={isMobile}>
                <Details>
                    {titles.map((title) => (
                        <EditableInput
                            key={title}
                            editMode={editMode}
                            readOnly={
                                readOnlyInputs.includes(title) ||
                                (title === 'Insurance_org_Code' && hasOrgCode)
                            }
                            name={title}
                            label={i18n.t(`${itemName}.${title}`)}
                            LabelComponent={() => (
                                <TextTitle>
                                    {i18n.t(`${itemName}.${title}`)}
                                </TextTitle>
                            )}
                            orientation="vertical"
                            formControl={formControl}
                            formErrors={formErrors}
                        />
                    ))}
                </Details>

                <Divider orientation="vertical" variant="middle" flexItem />
                <ImageContainer isMobile={isMobile}>
                    <ImageUploadAvatar
                        src={image ?? item?.userPhoto}
                        onClick={() => {
                            console.log('analytics image clicked')
                            imageInputRef?.current?.click()
                        }}
                    />

                    {editMode && (
                        <Button
                            color="secondary"
                            component="label"
                            style={{color: '#85898c'}}
                            size="large"
                            onClick={() =>
                                analyticsService.profileSettingsPressed(
                                    'add_profile_picture'
                                )
                            }>
                            {i18n.t(`${itemName}.editImageText`)}

                            <input
                                ref={imageInputRef}
                                hidden
                                accept="image/*"
                                type="file"
                                onChange={onUpload}
                            />
                        </Button>
                    )}
                </ImageContainer>
            </Content>
            {error && <ErrorText>{error}</ErrorText>}
            <ButtonsWrapper>
                {allowEdit &&
                    (editMode ? (
                        <>
                            <CancelButton color="secondary" onClick={onCancel}>
                                {i18n.t('form.cancel')}
                            </CancelButton>

                            <SaveButton
                                color="secondary"
                                variant="contained"
                                onClick={handleSubmit(onSubmit)}>
                                {i18n.t('form.save')}
                            </SaveButton>
                        </>
                    ) : (
                        <SaveButton
                            startIcon={<EditIcon />}
                            color="secondary"
                            onClick={toggleEdit}>
                            {i18n.t(`${itemName}.editButtonText`)}
                        </SaveButton>
                    ))}
            </ButtonsWrapper>
        </Container>
    )
}

export default FormSettingsItem

const Container = styled(Accordion)`
    width: 100%;
`

const ItemHeader = styled(AccordionSummary)`
    background: ${Colors.CARD_TITLE_BACKGROUND};
`

const TitleText = styled(Texts.BoldText)`
    font-size: 20px;
`

const Title = styled('div')`
    display: flex;
    align-items: center;
    gap: 24px;
`

const Content = styled(AccordionDetails)`
    display: flex;
    flex-direction: ${({isMobile}) => (isMobile ? 'column-reverse' : 'row')};
    justify-content: space-between;
    padding: ${({isMobile}) => (isMobile ? 0 : 48)}px;
`

const Details = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 2;
    padding: 24px;
`

const TextTitle = styled(Texts.RegularText)`
    color: #85898c;
`

const ImageContainer = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 2;
    gap: 12px;
    margin-top: ${({isMobile}) => (isMobile ? 40 : 0)}px;
`

const ImageUploadAvatar = styled(Avatar)`
    height: 20vh;
    width: 20vh;
    border: ${`1px solid ${Colors.APP_GRAY_DARK}`};
`
const ErrorText = styled(FormErrorText)`
    display: flex;
    justify-content: center;
`

const SaveButton = styled(Button)``

const CancelButton = styled(Button)``

const ButtonsWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    padding: 24px;
    gap: 12px;
`
